import React, { useState, useRef, useEffect } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import {
	BsBell,
	BsCashCoin,
	BsDoorOpen,
	BsGear,
	BsGlobe,
	BsHouses,
	BsMegaphone,
	BsPeople,
	BsSliders,
	BsStar,
	BsChevronDown,
	BsEyedropper,
} from 'react-icons/bs';

import BayDrawer from '../../../BayDrawer';
import { colorsArr } from '../../../utilities/constants/launchpadConstants';
import ColorPickerPortal from '../ColorPickerPortal';

interface IconItem {
	id: string;
	icon: React.ReactElement;
}

interface ColorCodes {
	iconColor?: string;
	iconBgColor?: string;
	linkColor?: string;
}

interface SelectedTheme {
	colorName?: string;
	iconHexColor?: string;
}

interface SettingsDrawerProps {
	t: (key: string) => string;
	bannerType?: 'none' | 'single' | 'double';
	isSettingsDrawerOpen: boolean;
	setIsSettingsDrawerOpen: (open: boolean) => void;
	handleDrawerFinish: () => void;
	customToggle?: React.ComponentType<any>;
	customThemeEnabled: boolean;
	setCustomThemeEnabled: (enabled: boolean) => void;
	colorCodes: ColorCodes;
	handleThemeChange: (key: string) => void;
	debouncedUpdate: (value: string, type: string, customThemeEnabled: boolean) => void;
	selectedTheme: SelectedTheme;
}

const getWidgetIcons = (iconColor: string): IconItem[] => [
	{ id: 'people', icon: <BsPeople color={iconColor} size={16} /> },
	{ id: 'star', icon: <BsStar color={iconColor} size={16} /> },
	{ id: 'bell', icon: <BsBell color={iconColor} size={16} /> },
	{ id: 'houses', icon: <BsHouses color={iconColor} size={16} /> },
	{ id: 'gear', icon: <BsGear color={iconColor} size={16} /> },
	{ id: 'megaphone', icon: <BsMegaphone color={iconColor} size={16} /> },
	{ id: 'doorOpen', icon: <BsDoorOpen color={iconColor} size={16} /> },
	{ id: 'sliders', icon: <BsSliders color={iconColor} size={16} /> },
	{ id: 'cashCoin', icon: <BsCashCoin color={iconColor} size={16} /> },
	{ id: 'globe', icon: <BsGlobe color={iconColor} size={16} /> },
];

const SettingsDrawer = React.memo((props: SettingsDrawerProps) => {
	const {
		t,
		bannerType,
		isSettingsDrawerOpen,
		setIsSettingsDrawerOpen,
		handleDrawerFinish,
		customToggle,
		customThemeEnabled,
		setCustomThemeEnabled,
		colorCodes,
		handleThemeChange,
		debouncedUpdate,
		selectedTheme,
	} = props;

	const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);
	const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
	const [localColorState, setLocalColorState] = useState<ColorCodes>({});

	const iconColorRef = useRef<HTMLDivElement>(null);
	const bgColorRef = useRef<HTMLDivElement>(null);
	const linkColorRef = useRef<HTMLDivElement>(null);
	const drawerRef = useRef<HTMLElement | null>(null);

	const handleColorPickerOpen = (type: string, ref: React.RefObject<HTMLDivElement>) => {
		if (openColorPicker === type) {
			setOpenColorPicker(null);
			return;
		}

		const rect = ref.current?.getBoundingClientRect();
		if (rect) {
			const position = {
				top: rect.bottom + 8,
				left: rect.left,
			};
			setPickerPosition(position);
			setOpenColorPicker(type);
		}
	};

	const handleColorChange = (value: string, type: string | null) => {
		if (!type) return;

		const mappedType = type === 'bgColor' ? 'iconBgColor' : type;

		setLocalColorState((prev) => ({
			...prev,
			[mappedType]: value,
		}));
	};

	const handleColorChangeComplete = (value: string, type: string | null) => {
		if (!type) return;

		const mappedType = type === 'bgColor' ? 'iconBgColor' : type;
		debouncedUpdate(value, mappedType, customThemeEnabled);
	};

	useEffect(() => {
		if (isSettingsDrawerOpen) {
			const drawer = document.getElementById('launchpad_settings_drawer');
			if (drawer) {
				drawerRef.current = drawer;
			}
		}
	}, [isSettingsDrawerOpen]);

	const icons = getWidgetIcons(colorCodes?.iconColor || '');

	return (
		<BayDrawer
			bannerType={bannerType}
			className="color-picker-enabled-drawer"
			dynamicWidth={570}
			footerContent={
				<div className="settings-drawer-footer d-flex justify-content-between w-100 pt-3 pe-4 pb-4 ps-4">
					<button
						className="cancel-button btn btn-outline-secondary d-flex align-items-center"
						onClick={() => {
							setOpenColorPicker(null);
							setIsSettingsDrawerOpen(false);
						}}
					>
						<span className="fw-semibold fs-6">{t('cancel')}</span>
					</button>

					<button
						className="finish-button btn btn-primary d-flex align-items-center"
						onClick={() => {
							setOpenColorPicker(null);
							handleDrawerFinish();
						}}
					>
						<span className="fw-semibold fs-6">{t('finish')}</span>
					</button>
				</div>
			}
			id="launchpad_settings_drawer"
			launchpadConfig
			menuType="launchpad"
			openBool={isSettingsDrawerOpen}
			showFooter
			title={t('launchPadSettings')}
			zIndexBackdrop="var(--bs-zindex-in-modal-dropdown, 1060)"
			onCloseFunc={() => {
				setOpenColorPicker(null);
				setIsSettingsDrawerOpen(false);
			}}
		>
			<div className="settings-drawer-content d-flex flex-column gap-4 py-4 px-5">
				<div className="d-flex flex-column gap-3">
					<h1 className="fw-semibold fs-5 m-0">
						{t('launchpadSettingsDesc1')}
					</h1>
					<p className="fw-normal fs-6 m-0">{t('launchpadSettingsDesc2')}</p>
				</div>

				<div className="d-flex flex-column gap-3">
					<Form.Label className="fw-bold fs-6 mb-0 text-dark">
						{t('colorTheme')}
					</Form.Label>
					<Dropdown className="color-theme-dropdown d-flex flex-column align-items-center justify-content-center w-100 border rounded-1 border-secondary-subtle py-2 ps-1 pe-2">
						<Dropdown.Toggle
							as={customToggle}
							className="w-100 d-flex flex-column gap-2 align-items-center"
							variant="light"
						>
							{!customThemeEnabled ? (
								<div
									className={`d-flex justify-content-center align-items-center ${colorCodes?.iconBgColor === '#827e7c' ? ' border border-200' : ''} ms-2 rounded-1 bg-white me-2 text-700`}
								>
									<div
										className={`color-theme-dropdown-toggle ${colorCodes?.iconBgColor === '#827e7c' ? 'bg-white border border-200' : ''} rounded-1 cursor-pointer`}
										style={{ backgroundColor: colorCodes?.iconColor }}
									/>
								</div>
							) : null}
							<span
								className={`fs-6 fw-normal ${customThemeEnabled ? 'ps-2' : ''}`}
							>
								{customThemeEnabled
									? t('customTheme')
									: selectedTheme?.colorName}
							</span>
							<div className="border-start border-secondary-subtle h-100 ms-auto ps-2 d-flex align-items-center">
								<BsChevronDown color="#5D5F69" size={16} />
							</div>
						</Dropdown.Toggle>

						<Dropdown.Menu
							className="w-100 color-theme-dropdown-menu"
							popperConfig={{
								modifiers: [{ name: 'offset', options: { offset: [-4, 9] } }],
							}}
						>
							<div className="d-flex flex-column gap-3 p-3">
								<Form.Label className="fw-bold fs-6 mb-0 text-secondary">
									{t('presetThemes')}
								</Form.Label>
								<div className="d-flex flex-wrap">
									{Object.keys(colorsArr).map((key) => (
										<div
											key={key}
											className={`color-theme-dropdown-item d-flex align-items-center justify-content-center selector rounded-1 ${(colorsArr as any)[key]?.iconHexColor === selectedTheme?.iconHexColor ? 'border border-primary' : ''} cursor-pointer`}
											style={{ width: 24, height: 24 }}
											onClick={() => {
												handleThemeChange(key);
												setLocalColorState({});
												setCustomThemeEnabled(false);
											}}
										>
											<div
												className={`color-theme-dropdown-item-toggle rounded-1 ${(colorsArr as any)[key].colorName === 'Dark Mode' ? 'border border-200' : (colorsArr as any)[key]?.iconHexColor}`}
												style={{
													backgroundColor:
														(colorsArr as any)[key].colorName === 'Light Mode'
															? '#191C29'
															: (colorsArr as any)[key].colorName === 'Dark Mode'
																? '#FFFFFF'
																: (colorsArr as any)[key].iconHexColor,
													width: 20,
													height: 20,
												}}
											/>
										</div>
									))}
								</div>
								<div className="border-top mt-2 pt-2 d-flex align-items-center gap-2">
									<BsEyedropper color="var(--bs-primary)" size={16} />
									<span
										className="text-primary cursor-pointer"
										onClick={() => {
											setCustomThemeEnabled(!customThemeEnabled);
											setLocalColorState({});
										}}
									>
										{t('customTheme')}
									</span>
								</div>
							</div>
						</Dropdown.Menu>
					</Dropdown>

					{customThemeEnabled && (
						<div className="w-100 d-flex flex-row justify-content-between gap-3">
							<div className="w-50 d-flex flex-column gap-3">
								<Form.Label className="fw-bold fs-6 mb-0 text-dark">
									{t('iconColor')}
								</Form.Label>
								<div
									className="d-flex flex-row gap-2 align-items-center form-control ps-1 cursor-pointer color-swatch-container"
									data-type="iconColor"
									onClick={() =>
										handleColorPickerOpen('iconColor', iconColorRef)
									}
								>
									<div
										ref={iconColorRef}
										className="color-swatch"
										style={{
											backgroundColor:
												localColorState.iconColor || colorCodes?.iconColor,
											width: 24,
											height: 24,
											borderRadius: 4,
											cursor: 'pointer',
											border: '4px solid var(--bs-50)',
										}}
									/>
									<span className="fs-6 fw-normal">
										{localColorState.iconColor || colorCodes?.iconColor}
									</span>
								</div>
							</div>

							<div className="w-50 d-flex flex-column gap-3">
								<Form.Label className="fw-bold fs-6 mb-0 text-dark">
									{t('iconBgColor')}
								</Form.Label>
								<div
									className="d-flex flex-row gap-2 align-items-center form-control ps-1 cursor-pointer color-swatch-container"
									data-type="bgColor"
									onClick={() => handleColorPickerOpen('bgColor', bgColorRef)}
								>
									<div
										ref={bgColorRef}
										className="color-swatch"
										style={{
											backgroundColor:
												localColorState.iconBgColor || colorCodes?.iconBgColor,
											width: 24,
											height: 24,
											borderRadius: 4,
											cursor: 'pointer',
											border: '4px solid var(--bs-50)',
										}}
									/>
									<span className="fs-6 fw-normal">
										{localColorState.iconBgColor || colorCodes?.iconBgColor}
									</span>
								</div>
							</div>
						</div>
					)}

					<div className="d-flex flex-column gap-3">
						<Form.Label className="fw-normal fs-6 mb-0 text-dark">
							{t('iconExamples')}:
						</Form.Label>
						<div className="d-flex w-100 gap-2 rounded-1">
							{icons.map((icon) => (
								<div
									key={icon.id}
									className="icon-example-item p-2 d-flex justify-content-center align-items-center rounded-1"
									style={{
										backgroundColor:
											localColorState.iconBgColor || colorCodes?.iconBgColor,
									}}
								>
									{React.cloneElement(icon.icon as React.ReactElement, {
										color: localColorState.iconColor || colorCodes?.iconColor,
									} as any)}
								</div>
							))}
						</div>
					</div>

					{customThemeEnabled && (
						<div className="w-100 d-flex flex-column gap-3">
							<Form.Label className="fw-bold fs-6 mb-0 text-dark">
								{t('linkColor')}
							</Form.Label>
							<div
								className="d-flex flex-row gap-2 align-items-center form-control ps-1 cursor-pointer color-swatch-container"
								data-type="linkColor"
								onClick={() => handleColorPickerOpen('linkColor', linkColorRef)}
							>
								<div
									ref={linkColorRef}
									className="color-swatch"
									style={{
										backgroundColor:
											localColorState.linkColor || colorCodes?.linkColor,
										width: 24,
										height: 24,
										borderRadius: 4,
										cursor: 'pointer',
										border: '4px solid var(--bs-50)',
									}}
								/>
								<span className="fs-6 fw-normal">
									{localColorState.linkColor || colorCodes?.linkColor}
								</span>
							</div>
						</div>
					)}

					<Form.Label className="fw-normal fs-6 text-dark">
						{t('linkExamples')}:
					</Form.Label>
					<a
						className="text-decoration-none"
						href="#"
						style={{
							color: localColorState.linkColor || colorCodes?.linkColor,
						}}
					>
						Example Link 1
					</a>
				</div>
			</div>
			<ColorPickerPortal
				color={
					openColorPicker === 'iconColor'
						? colorCodes?.iconColor
						: openColorPicker === 'bgColor'
							? colorCodes?.iconBgColor
							: colorCodes?.linkColor
				}
				isOpen={!!openColorPicker}
				position={pickerPosition}
				onChange={(color) => handleColorChange(color, openColorPicker)}
				onChangeComplete={(color) =>
					handleColorChangeComplete(color, openColorPicker)
				}
				onClose={() => setOpenColorPicker(null)}
			/>
		</BayDrawer>
	);
});

export default SettingsDrawer;
